import React, { useState, useEffect } from 'react';
import { string, number, bool } from 'prop-types';
import { I18n } from '@fiverr-private/fiverr_context';
import { Icon, Button } from '@fiverr-private/fit';
import { fiverr_ligature_logo } from '@fiverr-private/fit/icons/logos';
import { r_times } from '@fiverr-private/fit/icons';
import { isMobile, isIos } from '../../lib/device';
import cookie from '../../lib/cookie';
import { getReviews } from './api';
import Reviews from './Reviews';
import Ratings from './Ratings';
import {
    OS_APP_DATA,
    DEFAULT_OS_APP,
    COOKIE_EXPIRES
} from './constants';
import './style.scss';

/**
 * Gets the proper OS App data by a given os name
 * @param {String} osName The detected os parsed name
 * @return {AppData}
 */
const getOsAppData = (osName) => OS_APP_DATA[osName] || OS_APP_DATA[DEFAULT_OS_APP];

/**
 * Serializes the layout data into a format accepted by the app banner component.
 * @param {Object} userAgentData The parsed ua parser user agent data.
 * @return {Object}
 */
export const serializer = ({ userAgentData }) => {
    const { device, os } = userAgentData;

    return {
        isMobile: isMobile(device.type),
        isIos: isIos(os.name),
        ...getOsAppData(os.name)
    };
};

/**
 * Checks if AppBanner component should be shown.
 * @param {Boolean} isMobile Whether current user agent is mobile.
 * @param {String} dismissCookie The app banner dismiss cookie name.
 * @param {String} headerCookies The current request headers cookie object.
 * @param {Boolean} knownCrawler Whether it's a known crawler.
 * @return {Boolean}
 */
export const show = ({ isMobile, dismissCookie, headerCookies, knownCrawler }) => {
    const cookies = cookie.getAll(headerCookies);

    return isMobile && !cookies[dismissCookie] && !knownCrawler;
};

const AppBanner = ({
    isIos = false,
    nativeAppUrl,
    dismissCookie,
    reviews: initialReviews,
    ratings: initialRatings
}) => {
    const [reviews, setReviews] = useState(initialReviews);
    const [ratings, setRatings] = useState(initialRatings);
    const [show, setShow] = useState(true);

    const title = `layout.app_banner.${isIos ? 'ios' : 'android'}_title`;

    const openInApp = () => window.location = nativeAppUrl;

    const onDismiss = () => {
        cookie.set(dismissCookie, true, COOKIE_EXPIRES);

        setShow(false);
    };

    useEffect(() => {
        const getOsReviews = async() => {
            const { reviews, ratings } = await getReviews(isIos);

            reviews && setReviews(reviews);
            ratings && setRatings(ratings);
        };

        getOsReviews();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return show ? (
        <div className="app-banner flex flex-justify-between flex-items-center">
            <div className="flex flex-items-center">
                <Icon size={50} className="app-banner-logo">{fiverr_ligature_logo}</Icon>
                <div>
                    <h6><I18n k={title}/></h6>
                    <div className="app-reviews flex flex-items-center">
                        <Ratings ratings={ratings}/>
                        <Reviews reviews={reviews}/>
                    </div>
                </div>
            </div>

            <Button className="open-in-app" onClick={openInApp} size="small">
                <I18n k="layout.app_banner.open_in_app"/>
            </Button>
            <Icon className="dismiss" size={11} onClick={onDismiss}>{r_times}</Icon>
        </div>
    ) : null;
};

AppBanner.propTypes = {
    nativeAppUrl: string,
    dismissCookie: string,
    reviews: number,
    ratings: number,
    isIos: bool
};

export default AppBanner;
